import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { getOilChangeVehicles, recordOilChange } from '../services/api';

const OilBadge = ({ oilChange }) => {
  if (!oilChange?.configured) {
    return <span className="badge">Not configured</span>;
  }
  if (oilChange.due) {
    return <span className="badge danger">Oil change due</span>;
  }
  return <span className="badge ok">OK</span>;
};

export default function OilChangePage() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [kmDraft, setKmDraft] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getOilChangeVehicles();
      const list = Array.isArray(data?.vehicles) ? data.vehicles : [];
      setVehicles(list);
      const drafts = {};
      list.forEach((v) => {
        const id = v.vehicleId;
        drafts[id] = v.currentMileage != null ? String(v.currentMileage) : '';
      });
      setKmDraft(drafts);
    } catch (err) {
      setError(err?.message || 'Failed to load vehicles');
      setVehicles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const pendingDue = vehicles.filter((v) => v.oilChange?.due);

  const handleRecord = async (row) => {
    const id = row.vehicleId;
    const km = kmDraft[id];
    if (km === '' || km == null) {
      toast.error('Enter odometer km when oil was changed');
      return;
    }

    setSavingId(id);
    try {
      await recordOilChange(id, { lastOilChangeMileage: Number(km) });
      toast.success(`${row.name || 'Vehicle'} — oil change recorded at ${km} km`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to record oil change');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <>
      <div className="page-header">
        <h2>Oil Change</h2>
        <p>
          For Zaman / guards — record when a vehicle&apos;s oil was changed. Enter the
          odometer reading at service. No admin login needed. Interval is set by admin in Manage.
        </p>
      </div>

      {!loading && pendingDue.length > 0 && (
        <div className="alert-banner suspicious" style={{ marginBottom: 16 }}>
          <span>🔴</span>
          <span>
            <strong>{pendingDue.length}</strong> vehicle{pendingDue.length === 1 ? '' : 's'} due
            for oil change — highlighted below.
          </span>
        </div>
      )}

      <div className="card">
        {loading ? (
          <p className="text-muted">Loading…</p>
        ) : error ? (
          <div className="empty-state">
            <div className="icon">⚠</div>
            <p className="text-muted">{error}</p>
          </div>
        ) : vehicles.length === 0 ? (
          <div className="empty-state">
            <div className="icon">🚗</div>
            <p>No vehicles in the fleet yet.</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Vehicle</th>
                  <th>Plate</th>
                  <th>Current km</th>
                  <th>Last oil km</th>
                  <th>Due at km</th>
                  <th>Status</th>
                  <th>Oil done at (km)</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {vehicles.map((row) => {
                  const oc = row.oilChange || {};
                  const isDue = Boolean(oc.due);
                  const rowClass = isDue ? 'wash-row-overdue' : '';
                  const id = row.vehicleId;

                  return (
                    <tr key={id} className={rowClass}>
                      <td>
                        <strong>{row.name || '—'}</strong>
                        {isDue && (
                          <span
                            className="text-sm"
                            style={{
                              display: 'block',
                              marginTop: 2,
                              color: 'var(--danger)',
                              fontWeight: 600,
                            }}
                          >
                            ⚠ Oil change due
                          </span>
                        )}
                      </td>
                      <td className="mono">{row.plate || '—'}</td>
                      <td className="mono">{row.currentMileage ?? '—'}</td>
                      <td className="mono">{row.lastOilChangeMileage ?? '—'}</td>
                      <td className="mono">{oc.dueAtMileage ?? '—'}</td>
                      <td><OilBadge oilChange={oc} /></td>
                      <td>
                        {oc.configured ? (
                          <input
                            type="number"
                            min="0"
                            step="1"
                            className="edit-input-style"
                            style={{ width: 100, padding: '4px 6px', fontSize: 13 }}
                            value={kmDraft[id] ?? ''}
                            onChange={(e) => setKmDraft((d) => ({ ...d, [id]: e.target.value }))}
                            placeholder="km"
                          />
                        ) : (
                          <span className="text-muted text-sm">Set interval in Manage</span>
                        )}
                      </td>
                      <td>
                        <button
                          type="button"
                          className={`btn btn-sm ${isDue ? 'btn-danger' : 'btn-primary'}`}
                          disabled={!oc.configured || savingId === id}
                          onClick={() => handleRecord(row)}
                        >
                          {savingId === id ? 'Saving…' : 'Record oil change'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
